import { InventoryUOMMapping, ProductDoc, ProductInventoryUnit } from '@gooduncles/gu-app-schema';
import { Col, Form, Input, InputNumber, Row, Select } from 'antd';
import { FC, useMemo } from 'react';

interface InventoryUOMConfigProps {
  product: ProductDoc;
  initialValues?: {
    inventoryUnit?: ProductInventoryUnit;
    uom?: InventoryUOMMapping;
  };
}

const InventoryUOMConfig: FC<InventoryUOMConfigProps> = ({ product, initialValues }) => {
  const eaDefaults = useMemo(() => {
    const uom = initialValues?.uom;
    return (
      uom?.[ProductInventoryUnit.EA] ?? {
        ratio: 1,
        cost: product.cost,
        barcode: '',
      }
    );
  }, [initialValues?.uom, product.cost]);

  const bundleDefaults = useMemo(() => {
    const uom = initialValues?.uom;
    return (
      uom?.[ProductInventoryUnit.BUNDLE] ?? {
        ratio: 5,
        cost: product.cost * 5,
        barcode: '',
      }
    );
  }, [initialValues?.uom, product.cost]);

  return (
    <>
      <h3 className='sectionTitle'>단위 등록 (Unit of Measure)</h3>
      <Form.Item
        name='inventoryUnit'
        initialValue={initialValues?.inventoryUnit || ProductInventoryUnit.EA}
        label='단위 타입'
        rules={[{ required: true, message: '필수 입력 항목입니다' }]}
        tooltip='하나의 단위는 필수입니다. EA 또는 Bundle 중 하나를 선택하세요.'>
        <Select
          options={[
            { label: '개별 단위 (EA)', value: ProductInventoryUnit.EA },
            { label: '묶음 단위 (Bundle)', value: ProductInventoryUnit.BUNDLE },
          ]}
        />
      </Form.Item>

      <Form.Item
        noStyle
        shouldUpdate={(prevValues, currentValues) => prevValues.inventoryUnit !== currentValues.inventoryUnit}>
        {({ getFieldValue }) => {
          const inventoryUnit = getFieldValue('inventoryUnit');
          return (
            <>
              {inventoryUnit === ProductInventoryUnit.EA && (
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      name='eaRatio'
                      label='비율'
                      initialValue={eaDefaults.ratio}
                      rules={[{ required: true, message: '필수 입력 항목입니다' }]}>
                      <InputNumber style={{ width: '100%' }} min={1} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name='eaCost' label='매입 원가' initialValue={eaDefaults.cost}>
                      <InputNumber style={{ width: '100%' }} min={0} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name='eaBarcode' label='바코드' initialValue={eaDefaults.barcode}>
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              {inventoryUnit === ProductInventoryUnit.BUNDLE && (
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      name='bundleRatio'
                      label='비율'
                      initialValue={bundleDefaults.ratio}
                      rules={[{ required: true, message: '필수 입력 항목입니다' }]}
                      tooltip='묶음 단위가 기본 단위(EA)의 몇 배인지 설정합니다'>
                      <InputNumber style={{ width: '100%' }} min={1} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name='bundleCost' label='매입 원가' initialValue={bundleDefaults.cost}>
                      <InputNumber style={{ width: '100%' }} min={0} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name='bundleBarcode' label='바코드' initialValue={bundleDefaults.barcode}>
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </>
          );
        }}
      </Form.Item>
    </>
  );
};

export default InventoryUOMConfig;
